import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { MapPin, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const COSTA_BRAVA_IMAGE = "https://images.unsplash.com/photo-1762254923872-5bdc4210eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85";

const locations = [
  { name: 'Empuriabrava', featured: true },
  { name: 'Roses', featured: true },
  { name: 'Figueres', featured: true },
  { name: 'Cadaqués', featured: true },
  { name: 'Santa Margarita', featured: false },
  { name: 'Llançà', featured: false },
  { name: 'Castelló', featured: false },
  { name: 'L\'Escala', featured: false },
];

// 3D Location Pin
const LocationPin3D = ({ scrollYProgress, position }) => {
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-30, 0, 30]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.2, 0.9]);

  return (
    <motion.div
      className="absolute hidden xl:block pointer-events-none"
      style={{ ...position, rotateY, y, scale, transformStyle: 'preserve-3d' }}
    >
      <svg width="50" height="70" viewBox="0 0 50 70">
        <defs>
          <linearGradient id="pinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#0284c7" />
          </linearGradient>
          <filter id="pinShadow">
            <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="rgba(14,165,233,0.4)" />
          </filter>
        </defs>
        <path
          d="M25 0 C38 0 50 12 50 25 C50 45 25 70 25 70 C25 70 0 45 0 25 C0 12 12 0 25 0"
          fill="url(#pinGradient)"
          filter="url(#pinShadow)"
        />
        <circle cx="25" cy="25" r="10" fill="white" />
      </svg>
    </motion.div>
  );
};

// 3D Location Card
const LocationCard3D = ({ location, index }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const col = index % 2;
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [col === 0 ? -15 : 15, 0, col === 0 ? 5 : -5]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [50, 0, 0, -30]);
  const x = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [col === 0 ? -30 : 30, 0, 0, col === 0 ? -15 : 15]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.6]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.9, 1, 1, 0.95]);

  const springConfig = { stiffness: 80, damping: 20 };
  const ySpring = useSpring(y, springConfig);
  const xSpring = useSpring(x, springConfig);

  return (
    <motion.div
      ref={cardRef}
      style={{
        rotateY,
        y: ySpring,
        x: xSpring,
        opacity,
        scale,
        transformStyle: 'preserve-3d',
      }}
      className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 lg:p-5 rounded-xl sm:rounded-2xl backdrop-blur-md transition-all ${
        location.featured 
          ? 'bg-sky-500/20 border border-sky-400/30' 
          : 'bg-white/10 border border-white/10'
      } hover:bg-sky-500/30`}
      data-testid={`location-${location.name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <motion.div 
        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          location.featured ? 'bg-sky-500' : 'bg-slate-700'
        }`}
        whileHover={{ scale: 1.2, rotate: 10 }}
      >
        {location.featured ? (
          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        ) : (
          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
        )}
      </motion.div>
      <span className={`font-semibold text-sm sm:text-base ${
        location.featured ? 'text-white' : 'text-slate-300'
      }`}>
        {location.name}
      </span>
    </motion.div>
  );
};

// Stat counter with 3D effect
const StatCounter3D = ({ number, suffix, label, index }) => {
  const statRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: statRef,
    offset: ['start end', 'center center'],
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [30, 0, -15]);
  const y = useTransform(scrollYProgress, [0, 0.5], [40, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.4], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.1, 1]);

  const ySpring = useSpring(y, { stiffness: 100, damping: 25 });

  return (
    <motion.div
      ref={statRef}
      style={{ rotateX, y: ySpring, opacity, scale, transformStyle: 'preserve-3d' }}
      className="text-center px-4"
    >
      <div className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl text-sky-400">
        {number}{suffix}
      </div>
      <div className="text-xs sm:text-sm text-slate-400 mt-1">{label}</div>
    </motion.div>
  );
};

export const LocationParallax = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const bgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1.15, 1.1]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['5%', '-5%']);

  const bgYSpring = useSpring(bgY, { stiffness: 80, damping: 25 });

  return (
    <section 
      ref={containerRef}
      className="relative py-20 sm:py-28 lg:py-40 overflow-hidden"
      data-testid="location-section"
    >
      {/* Parallax Background */}
      <motion.div className="absolute inset-0" style={{ y: bgYSpring }}>
        <motion.div style={{ scale: bgScale }} className="absolute inset-0">
          <img 
            src={COSTA_BRAVA_IMAGE}
            alt="Costa Brava"
            className="w-full h-[130%] object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50" />
      </motion.div>

      {/* 3D Pins */}
      <LocationPin3D scrollYProgress={scrollYProgress} position={{ top: '20%', right: '15%' }} />
      <LocationPin3D scrollYProgress={scrollYProgress} position={{ bottom: '30%', left: '10%' }} />

      {/* Floating bubbles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-sky-400/10 backdrop-blur-sm"
            style={{
              width: 15 + Math.random() * 40,
              height: 15 + Math.random() * 40,
              left: `${Math.random() * 100}%`,
              bottom: -30,
            }}
            animate={{
              y: [0, -window.innerHeight - 100],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 10 + Math.random() * 8,
              repeat: Infinity,
              delay: i * 1,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <motion.div 
        style={{ y: contentY }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-20 items-center">
          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 bg-sky-500/20 text-sky-300 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8 backdrop-blur-sm border border-sky-400/20"
            >
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
              Zona de Cobertura
            </motion.span>

            <h2 className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-4 sm:mb-6 lg:mb-8 tracking-tight leading-tight">
              Cubrimos toda la{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400">
                Costa Brava
              </span>
            </h2>

            <p className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 lg:mb-10 leading-relaxed">
              Desde Empuriabrava hasta Cadaqués, estamos presentes en las 
              principales localidades del Alt Empordà.
            </p>

            {/* Stats */}
            <div className="flex items-center justify-center lg:justify-start gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-10 flex-wrap">
              <StatCounter3D number="+15" suffix="" label="Años" index={0} />
              <div className="w-px h-12 sm:h-16 bg-slate-700 hidden sm:block" />
              <StatCounter3D number="500" suffix="+" label="Piscinas" index={1} />
              <div className="w-px h-12 sm:h-16 bg-slate-700 hidden sm:block" />
              <StatCounter3D number="100" suffix="%" label="Satisfacción" index={2} />
            </div>

            <Link
              to="/contacto"
              data-testid="location-cta"
              className="inline-flex items-center gap-2 sm:gap-3 bg-sky-500 hover:bg-sky-400 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-base transition-all shadow-xl hover:scale-105"
            >
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Consulta tu zona</span>
            </Link>
          </motion.div>

          {/* Location cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {locations.map((location, index) => (
              <LocationCard3D key={location.name} location={location} index={index} />
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default LocationParallax;
